import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";

export interface EditableSelectOption {
  value: string;
  label: string;
}

interface EditableSelectCellProps {
  value: string;
  options: EditableSelectOption[];
  onChange: (value: string) => void;
  className?: string;
  isEditingCell?: boolean;
  onStartEdit?: () => void;
  onEndEdit?: () => void;
}

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

const EDITABLE_CONTROL_SELECTOR = 'input[data-testid="editable-cell-input"], [data-editable-select-combobox="true"]';

export function EditableSelectCell({
  value,
  options,
  onChange,
  className,
  isEditingCell,
  onStartEdit,
  onEndEdit
}: EditableSelectCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const selectedOption = options.find((opt) => opt.value === value);
  const selectedIndex = options.findIndex((opt) => opt.value === value);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width
    });
  }, []);

  useEffect(() => {
    setIsOpen(isEditingCell ?? false);
  }, [isEditingCell]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [isOpen, selectedIndex, updatePosition]);

  useEffect(() => {
    if (isOpen && focusedIndex >= 0) {
      const el = optionRefs.current[focusedIndex];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    }
  }, [focusedIndex, isOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
    setDropdownPos(null);
    onEndEdit?.();
  }, [onEndEdit]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        close();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen) return;

    const handleScrollOrResize = () => close();

    window.addEventListener("resize", handleScrollOrResize);

    const scrollParents: HTMLElement[] = [];
    let el = triggerRef.current?.parentElement;
    while (el) {
      const { overflow, overflowX, overflowY } = getComputedStyle(el);
      if ([overflow, overflowX, overflowY].some((v) => v === "auto" || v === "scroll")) {
        scrollParents.push(el);
        el.addEventListener("scroll", handleScrollOrResize);
      }
      el = el.parentElement;
    }

    return () => {
      window.removeEventListener("resize", handleScrollOrResize);
      scrollParents.forEach((p) => p.removeEventListener("scroll", handleScrollOrResize));
    };
  }, [isOpen, close]);

  const handleOpen = () => {
    if (!isOpen) {
      setIsOpen(true);
      onStartEdit?.();
    }
  };

  const handleSelect = (optionValue: string) => {
    if (optionValue !== value) {
      onChange(optionValue);
    }
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleOpen();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) => (prev < options.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < options.length) {
          handleSelect(options[focusedIndex].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        close();
        break;
      case "Tab": {
        const current = e.currentTarget as HTMLElement;
        const table = current.closest("table");
        if (!table) {
          close();
          break;
        }

        const controls = Array.from(table.querySelectorAll<HTMLElement>(EDITABLE_CONTROL_SELECTOR));
        const currentIndex = controls.indexOf(current);
        if (currentIndex === -1) {
          close();
          break;
        }

        const targetIndex = currentIndex + (e.shiftKey ? -1 : 1);
        if (targetIndex < 0 || targetIndex >= controls.length) {
          // Let the browser handle leaving the table.
          close();
          break;
        }

        e.preventDefault();
        close();
        requestAnimationFrame(() => {
          const updatedTable = current.closest("table") ?? table;
          const updatedControls = Array.from(updatedTable.querySelectorAll<HTMLElement>(EDITABLE_CONTROL_SELECTOR));
          updatedControls[targetIndex]?.focus();
        });
        break;
      }
    }
  };

  const dropdown =
    isOpen && dropdownPos
      ? createPortal(
          <div
            ref={dropdownRef}
            role="listbox"
            style={{
              position: "absolute",
              top: dropdownPos.top + 4,
              left: dropdownPos.left,
              width: Math.max(dropdownPos.width, 140)
            }}
            className={classNames("z-50", "bg-sys-brand-surface border rounded shadow-lg", "max-h-60 overflow-auto")}
          >
            {options.map((option, index) => (
              <div
                key={option.value}
                ref={(el) => (optionRefs.current[index] = el)}
                role="option"
                aria-selected={option.value === value}
                onClick={() => handleSelect(option.value)}
                className={classNames(
                  "px-4 py-4 text-[1rem] cursor-pointer",
                  "hover:bg-sys-brand-surface-container-high",
                  option.value === value && "bg-sys-brand-surface-container",
                  focusedIndex === index && "bg-sys-brand-surface-container-high"
                )}
              >
                {option.label}
              </div>
            ))}
          </div>,
          document.body
        )
      : null;

  return (
    <div className="relative -ml-2.5" data-testid="editable-select-cell">
      <div
        ref={triggerRef}
        data-editable-select-combobox="true"
        role="combobox"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={handleOpen}
        onFocus={() => {
          onStartEdit?.();
        }}
        onKeyDown={handleKeyDown}
        className={classNames(
          "group/select-cell w-full px-2 py-3 outline-none flex items-center justify-between gap-1",
          "border-2 bg-transparent",
          "hover:bg-sys-brand-surface-container-high rounded-md",
          "focus:bg-sys-brand-surface-container-high focus:border-sys-brand-primary",
          className,
          isOpen ? "border-sys-brand-primary" : "border-transparent cursor-pointer"
        )}
      >
        <span className="truncate">{selectedOption?.label ?? value}</span>
        <div
          className={classNames(
            "shrink-0 self-center",
            "border-l-4 border-r-4 border-t-4",
            "border-l-transparent border-r-transparent border-t-sys-brand-on-surface-variant",
            "w-2 transition-opacity",
            isOpen
              ? "opacity-100"
              : "opacity-0 group-hover/select-cell:opacity-100 group-focus-within/select-cell:opacity-100"
          )}
        />
      </div>
      {dropdown}
    </div>
  );
}
